import { useEffect, useRef } from "react";

import styles from "./SlashCommandMenu.module.css";
import { Kbd } from "../../components/Kbd";
import type { ApiAgentCommand } from "../../data/api/chat";
import { cx } from "../../lib/cx";

export type SlashCommandMenuProps = {
  /** Index into `commands` of the row Enter and Tab would take. */
  readonly active: number;
  readonly commands: ReadonlyArray<ApiAgentCommand>;
  readonly onHover: (index: number) => void;
  readonly onPick: (command: ApiAgentCommand) => void;
};

/**
 * The `/` completion list above the composer (chat.md §Slash commands).
 *
 * Presentation only: which commands match, which one is active, and what
 * picking one does are all the composer's, because the keyboard belongs to the
 * textarea that still holds focus — a menu that took focus would break typing
 * the very name it is completing.
 */
export const SlashCommandMenu = ({
  active,
  commands,
  onHover,
  onPick,
}: SlashCommandMenuProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the active row in view as the arrows walk past the visible window.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-i="${active.toString()}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  return (
    // `u-pop` is the entrance every other popup surface uses (`Menu`,
    // `Popover`, the runner-enrolment panel), and it is gated on
    // `prefers-reduced-motion` — so matching it is also how this menu inherits
    // that (frontend.md §Accessibility).
    <div className={cx(styles.menu, "u-pop")}>
      <div className={styles.list} ref={listRef}>
        {commands.map((command, i) => (
          <button
            className={cx(styles.item, i === active && styles.active)}
            data-i={i}
            key={command.name}
            // `mousedown`, not `click`: the textarea keeps focus only if the
            // press never gets as far as blurring it, and a click that blurs
            // the composer loses the caret the completion is written at.
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(command);
            }}
            onMouseEnter={() => onHover(i)}
            type="button"
          >
            <span className={styles.name}>/{command.name}</span>
            {command.argument_hint ? (
              <span className={styles.hint}>{command.argument_hint}</span>
            ) : null}
            <span className={styles.description} title={command.description}>
              {command.description}
            </span>
          </button>
        ))}
      </div>
      {/* Clauses joined with `·`, like the needs-input card's own footer hint
          — three run together are one long word at a glance. */}
      <div className={styles.footer}>
        <Kbd>↑↓</Kbd> to choose <span className={styles.sep}>·</span>{" "}
        <Kbd>tab</Kbd> to complete <span className={styles.sep}>·</span>{" "}
        <Kbd>esc</Kbd> to dismiss
      </div>
    </div>
  );
};
